import { useState, useMemo, useEffect, useCallback } from 'react'
import { OverlayPortal } from '../../components/ModalPortal'
import useMenuBack from '../../hooks/useMenuBack'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, AlertTriangle, Lock, Eye, EyeOff, Download, Check, Shield } from 'lucide-react'
import { useUserStore, useProfileStore } from '../../stores'
import { useI18n } from '../../i18n'
import { authAPI } from '../../services/api'
import { generateRecordsPackage } from '../../utils/recordsPackage'
import { getFreshProviderToken, reauthMethodFor, serverAskedMethods, providerName, preloadProviderSdk } from '../../utils/ssoReauth'
import { saveOrShareBlob } from '../../utils/saveOrShareBlob'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

/**
 * DeleteAccountScreen v242.5.0
 *
 * Real, end-to-end account deletion (App Store 5.1.1(v) + HIPAA retention):
 *   1. Optional but encouraged: download a full records package first
 *      (reuses generateRecordsPackage — same zip as Settings).
 *   2. Re-authenticate. ★ A PASSWORD ACCOUNT PROVES A PASSWORD; AN SSO-ONLY
 *      ACCOUNT PROVES A FRESH Sign in with Apple/Google WHOSE `sub` MATCHES THE
 *      ONE ON THE ACCOUNT (API v243.104.0). v242.50.0 made the password
 *      optional for SSO and sent `confirm: true` instead — and the server
 *      accepted that boolean as the ENTIRE authentication, so an unlocked phone
 *      was one tap from an irreversible deletion. `confirm` is now intent only.
 *   3. POST /account/delete → wipes the login credentials, disposes of the
 *      patient's cases so the MD Panel stops listing them as active work, and
 *      logs a retention-flagged request; clinical records are retained.
 *   4. Log out and return to sign-in.
 */

const NAVY = '#15354F'
const BLUE = '#46A8F9'
const ICE = '#E9F8FF'
const LBLUE = '#A7D9FC'
const LINE = '#E2E8F0'
const SLATE = '#64748B'
const SLATE4 = '#94A3B8'

const REASON_KEYS = [
  'deleteReasonNoLongerNeed', 'deleteReasonSwitching', 'deleteReasonPrivacy',
  'deleteReasonNotifications', 'deleteReasonTechnical', 'deleteReasonOther',
]

export default function DeleteAccountScreen() {
  const { t } = useI18n()
  const navigate = useNavigate()
  const location = useLocation()
  const goBack = useMenuBack()

  // v242.49.0: AI-consent withdrawal (Settings → AI & Data) routes here with a
  // preset, locked reason so the deletion is attributed to consent withdrawal.
  const presetReason = location.state?.presetReason || ''
  const reasonLocked = !!presetReason

  const REASONS = useMemo(() => REASON_KEYS.map(k => t('settings.' + k)), [t])
  const logout = useUserStore((state) => state.logout)
  const user = useUserStore((state) => state.user)
  const profile = useProfileStore((state) => state.profile)

  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [showReasonModal, setShowReasonModal] = useState(false)
  const [reason, setReason] = useState(presetReason)
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  // The account's auth_provider as the server reports it ("email", "google,apple",
  // "email,apple", …). null = not read (yet, or the read failed).
  const [accountAuth, setAccountAuth] = useState(null)
  const [providersKnown, setProvidersKnown] = useState(false)
  // ★★★ v242.132.0 — WE COULD NOT READ THE ACCOUNT TYPE, WHICH IS NOT THE SAME AS
  //     "IT HAS A PASSWORD". The status fetch below used to fail into an empty
  //     `ssoProviders`, so an SSO-only account was treated as a password account:
  //     no re-auth token was sent, the server answered `reauth_required`, and the
  //     handler at the bottom of confirmDelete then LEARNED the provider from that
  //     failure and re-rendered the screen with the Google wording. A patient
  //     reported exactly that pairing — the Google copy and "try once more"
  //     together — as "delete does not work". It required a guaranteed failed
  //     attempt to discover what the account was. Tracking the failure explicitly
  //     lets us retry the read instead of failing the deletion to learn it.
  const [providersError, setProvidersError] = useState(false)
  const [reauthing, setReauthing] = useState(false)
  // ★★★ PROD-943b — CONFIRM THE WAY THE PATIENT SIGNED IN (Alex, 2026-10-13, Q53).
  //     An Apple session confirms with Apple, a Google session with Google, an
  //     e-mail session with the password: exactly that one, and no choice. Only a
  //     session from before this build (no `signInProvider`) still gets the PROD-943
  //     behaviour — a choice between two linked providers, or the password.
  //     PROD-943 had preselected the session's provider but still offered both.
  const [chosenProvider, setChosenProvider] = useState(null)
  // What the server asked for after it refused an attempt (serverAskedMethods); it
  // outranks the session, so a method the server will not take never dead-ends.
  const [serverMethods, setServerMethods] = useState(null)
  const sessionProvider = user?.signInProvider || null
  const plan = reauthMethodFor(providersError ? null : accountAuth, { session: sessionProvider, chosen: chosenProvider, server: serverMethods })
  const reauthProvider = plan.method === 'apple' || plan.method === 'google' ? plan.method : null
  const offerChoice = plan.choices.length > 1
  const isSsoOnly = !!reauthProvider || offerChoice
  // ★ A password is required only when we KNOW this is a password confirmation. If
  //   the read failed and the session is unknown we know nothing, and demanding a
  //   password an SSO-only account does not have is the same wrong guess in a
  //   different place.
  const passwordRequired = plan.method === 'password'
  const choiceMissing = offerChoice && !reauthProvider
  const providerLabel = providerName(reauthProvider)

  const loadProviders = useCallback(async () => {
    setProvidersError(false)
    try {
      const st = await authAPI.get2faStatus()
      setAccountAuth(String(st?.authProvider || ''))
      setProvidersKnown(true)
      return true
    } catch (err) {
      console.error('[DeleteAccount] could not read account type:', err?.status, err)
      // ★ NOT "it has a password". Unknown, and the screen says so.
      setProvidersKnown(true)
      setProvidersError(true)
      return false
    }
  }, [])

  useEffect(() => { loadProviders() }, [loadProviders])
  // PROD-1159: the provider's web SDK is loaded before the tap, so "Yes, delete" reaches the
  // sign-in popup with no await in front of it (a popup opened later can be blocked silently).
  useEffect(() => { if (reauthProvider) preloadProviderSdk(reauthProvider) }, [reauthProvider])

  // Records export (reuses the Settings packager)
  const [dl, setDl] = useState({ state: 'idle', pct: 0, msg: '', error: '', filename: '' })

  const runExport = async () => {
    if (dl.state === 'working') return
    setDl({ state: 'working', pct: 0, msg: t('settings.preparing') || 'Preparing your records…', error: '', filename: '' })
    try {
      const { blob, filename } = await generateRecordsPackage({
        profile,
        user,
        onProgress: (msg, pct) => setDl((d) => ({ ...d, msg, pct })),
      })
      await saveOrShareBlob(blob, filename)
      setDl({ state: 'done', pct: 100, msg: '', error: '', filename })
    } catch (e) {
      setDl({ state: 'error', pct: 0, msg: '', error: e?.message || (t('settings.somethingWentWrong') || 'Something went wrong.'), filename: '' })
    }
  }

  const handleDelete = () => {
    if (deleting) return
    // ★ A password account must actually type its password. Gating only on
    //   `deleting` (v242.50.0) is what made deletion look password-free.
    if (passwordRequired && !password) {
      setDeleteError(t('settingsMisc.enterPasswordToConfirm') || 'Enter your password to confirm')
      return
    }
    // PROD-943: two linked providers and no choice yet — never guess one.
    if (choiceMissing) {
      setDeleteError(t('settingsMisc.reauthCancelled') || "Sign in with your provider to confirm it's you.")
      return
    }
    setDeleteError('')
    setShowConfirmModal(true)
  }

  const confirmDelete = async () => {
    if (deleting) return
    setDeleting(true)
    setDeleteError('')
    // What this attempt sent, for reading a refusal: 'password' unless a provider was asked.
    const attempt = { method: 'password', fromSession: false }
    try {
      // ★★ If the account type never loaded, read it NOW rather than guessing —
      //    guessing costs the patient a failed deletion attempt to find out.
      let auth = providersError ? null : accountAuth
      if (providersError) {
        await loadProviders()
        try {
          const st = await authAPI.get2faStatus()
          auth = String(st?.authProvider || '')
          setAccountAuth(auth)
          setProvidersError(false)
        } catch { /* still unknown — fall through; the server is the authority */ }
      }
      const now = reauthMethodFor(auth, { session: sessionProvider, chosen: chosenProvider, server: serverMethods })
      attempt.fromSession = now.fromSession

      // An SSO confirmation proves identity with a FRESH provider sign-in, right now.
      const payload = { password, reason, confirm: true }
      if (now.method === 'apple' || now.method === 'google') {
        const provider = now.method
        attempt.method = provider
        setReauthing(true)
        try {
          payload.reauthProvider = provider
          payload.reauthToken = await getFreshProviderToken(provider)
        } finally {
          setReauthing(false)
        }
      } else if (now.choices.length) {
        // PROD-943: two linked providers and no choice yet — never guess one.
        setShowConfirmModal(false)
        setDeleting(false)
        setDeleteError(t('settingsMisc.reauthCancelled') || "Sign in with your provider to confirm it's you.")
        return
      }
      await authAPI.deleteAccount(payload)
      logout()
      try { sessionStorage.clear() } catch { /* no-op */ }
      navigate('/signin', { replace: true })
    } catch (e) {
      setShowConfirmModal(false)
      setDeleting(false)
      // v242.5.2: apiFetch throws { status, ...body }; the server puts the reason
      // on `error` (+ machine-readable `errorType`), never on `message`. Read those
      // so a wrong password shows the real reason instead of the generic fallback.
      const type = e?.errorType || ''
      const serverMsg = e?.error || e?.message || ''
      // The re-auth popup was dismissed — not an error, just not proven.
      if (e?.message === 'cancelled') {
        setDeleteError(t('settingsMisc.reauthCancelled')
          || `Sign in with ${providerLabel} to confirm it's you.`)
        return
      }
      // ★★ PROD-943b — THE SERVER ASKS FOR ANOTHER METHOD: FALL BACK, NEVER DEAD-END.
      //    An Apple/Google session on an account that also has e-mail linked sends the
      //    session's provider, and API e5c7cdb6 still demands the password there
      //    (`wrong_password` with no password sent). Until the API build that accepts
      //    the session's provider ships, the screen switches to the password field.
      if (type === 'wrong_password' && attempt.method !== 'password') {
        setServerMethods(['password'])
        setDeleteError(t('settingsMisc.enterPasswordToConfirm') || 'Enter your password to confirm')
        return
      }
      if (type === 'reauth_required' || type === 'reauth_failed') {
        // The server may ask for a method this screen did not use. A session's own
        // method is replaced only when the server's list leaves it out; a session from
        // before this build takes the server's list as it is (as PROD-943 did).
        const asked = serverAskedMethods(e?.providers)
        if (asked.length && (!attempt.fromSession || !asked.includes(attempt.method))) setServerMethods(asked)
        setDeleteError(type === 'reauth_failed'
          ? (t('settingsMisc.reauthMismatch') || "That sign-in didn't match this account.")
          : (t('settingsMisc.reauthNeeded') || "Sign in again to confirm it's you, then try once more."))
        return
      }
      const isWrongPw = type === 'wrong_password' || e?.status === 401 || /password/i.test(serverMsg)
      setDeleteError(
        isWrongPw
          ? (t('settingsMisc.incorrectPassword') || 'Incorrect password. Please try again.')
          : (serverMsg || (t('settings.somethingWentWrong') || 'Something went wrong. Please try again.'))
      )
    }
  }

  return (
    <div className="min-h-full bg-white flex flex-col">
      {/* Header */}
      <div className="sticky-page-header px-4 pt-4 pb-2 flex items-center gap-2.5">
        <button onClick={() => goBack()} className="p-0" style={{ background: 'none', border: 'none' }}>
          <ArrowLeft size={18} color="#64748B" strokeWidth={2.5} />
        </button>
        <span style={{ fontSize: 17, fontWeight: 700, color: NAVY }}>{t('settingsMisc.account') || 'Account'}</span>
      </div>

      <div className="flex-1 px-4 pb-8 overflow-auto">
        {/* Centered warning */}
        <div className="text-center" style={{ padding: '18px 0 14px' }}>
          <div className="inline-flex items-center justify-center"
            style={{ width: 56, height: 56, borderRadius: 999, background: '#FEF2F2', marginBottom: 12 }}>
            <AlertTriangle size={24} color="#DC2626" strokeWidth={2} />
          </div>
          <p style={{ fontSize: 18, fontWeight: 700, color: NAVY, margin: 0 }}>{t('settingsMisc.deleteAccount') || 'Delete your account'}</p>
          <p style={{ fontSize: 12, color: SLATE, margin: '6px 0 0' }}>{t('settingsMisc.wereSorryToSeeYou') || "We're sorry to see you go."}</p>
        </div>

        {/* Step 1 — download records */}
        <div style={{ background: ICE, border: `1px solid ${LBLUE}`, borderRadius: 14, padding: 14, marginBottom: 14 }}>
          <div className="flex gap-2.5">
            <Download size={20} color={BLUE} className="flex-shrink-0" style={{ marginTop: 1 }} />
            <div>
              <p style={{ fontSize: 13.5, fontWeight: 700, color: NAVY, margin: '0 0 3px' }}>
                {t('settingsMisc.downloadRecordsFirst') || 'Download your records first'}
              </p>
              <p style={{ fontSize: 11.5, color: '#42688a', margin: 0, lineHeight: 1.5 }}>
                {t('settingsMisc.downloadRecordsBody') ||
                  "Save a complete copy of your cases, visits, profile, and uploaded documents. Once deletion begins, anything not legally retained can't be recovered."}
              </p>
            </div>
          </div>

          {dl.state !== 'done' && (
            <button
              onClick={runExport}
              disabled={dl.state === 'working'}
              style={{
                marginTop: 12, width: '100%', border: 'none', borderRadius: 11, padding: 11,
                background: BLUE, color: '#fff', fontWeight: 700, fontSize: 13, cursor: dl.state === 'working' ? 'default' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: dl.state === 'working' ? 0.7 : 1,
              }}
            >
              {dl.state === 'working'
                ? <><OrellaSpinner size={16} tone="inherit" /> {dl.msg || (t('settings.preparing') || 'Preparing…')}</>
                : <><Download size={16} color="#fff" /> {t('settingsMisc.downloadMyRecords') || 'Download my records'}</>}
            </button>
          )}

          {dl.state === 'working' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ height: 7, borderRadius: 99, background: '#cfeafe', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${dl.pct || 0}%`, background: BLUE, borderRadius: 99, transition: 'width .25s ease' }} />
              </div>
            </div>
          )}

          {dl.state === 'done' && (
            <div className="flex items-center gap-2" style={{ marginTop: 12, background: '#fff', border: `1px solid ${LBLUE}`, borderRadius: 11, padding: '10px 12px' }}>
              <Check size={18} color={BLUE} strokeWidth={2.4} className="flex-shrink-0" />
              <div>
                <div style={{ fontSize: 11.5, fontWeight: 700, color: NAVY, fontFamily: 'ui-monospace, Menlo, monospace' }}>{dl.filename}</div>
                <div style={{ fontSize: 10, color: SLATE, marginTop: 1 }}>{t('settingsMisc.savedToDevice') || 'Saved to your device'}</div>
              </div>
            </div>
          )}

          {dl.state === 'error' && (
            <p style={{ marginTop: 10, fontSize: 11, color: '#DC2626' }}>{dl.error}</p>
          )}
        </div>

        {/* Retention disclosure — neutral slate */}
        <div style={{ background: '#F8FAFC', border: `1px solid ${LINE}`, borderRadius: 14, padding: 14, marginBottom: 16 }}>
          <div className="flex gap-2.5">
            <Shield size={18} color={NAVY} className="flex-shrink-0" style={{ marginTop: 1 }} />
            <div>
              <p style={{ fontSize: 12.5, fontWeight: 700, color: NAVY, margin: '0 0 4px' }}>
                {t('settingsMisc.whatHappensTitle') || 'What happens when you delete'}
              </p>
              <ul style={{ margin: '0', paddingLeft: 16 }}>
                <li style={{ fontSize: 11, color: SLATE, lineHeight: 1.5, marginBottom: 3 }}>
                  {t('settingsMisc.whatHappens1') || "Your account is deactivated right away and you're signed out."}
                </li>
                <li style={{ fontSize: 11, color: SLATE, lineHeight: 1.5, marginBottom: 3 }}>
                  {t('settingsMisc.whatHappens2') || 'We begin deleting your account and personal data.'}
                </li>
                <li style={{ fontSize: 11, color: SLATE, lineHeight: 1.5 }}>
                  {t('settingsMisc.whatHappens3') || 'Some medical records must be kept for up to 10 years, as required by law (HIPAA and state medical-record retention). These are locked and never used for anything else.'}
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Re-authentication — a password, or a fresh provider sign-in */}
        {isSsoOnly ? (
          <div style={{ border: `1.5px solid ${LBLUE}`, background: ICE, borderRadius: 12, padding: 14 }}>
            <div className="flex gap-2.5">
              <Shield size={18} color={BLUE} className="flex-shrink-0" style={{ marginTop: 1 }} />
              <div>
                <p style={{ fontSize: 12.5, fontWeight: 700, color: NAVY, margin: '0 0 3px' }}>
                  {t('settingsMisc.confirmItsYou') || "Confirm it's you"}
                </p>
                {reauthProvider && (
                  <p style={{ fontSize: 11, color: SLATE, margin: 0, lineHeight: 1.45 }}>
                    {t('settingsMisc.reauthWithProvider', { provider: providerLabel })
                      || `You'll be asked to sign in with ${providerLabel} before your account is deleted.`}
                  </p>
                )}
              </div>
            </div>
            {/* PROD-943: one button per linked provider, Apple first as on sign-in —
                only when the session's sign-in method is unknown (PROD-943b). */}
            {offerChoice && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
                {['apple', 'google'].filter((p) => plan.choices.includes(p)).map((p) => {
                  const selected = reauthProvider === p
                  return (
                    <button
                      key={p}
                      type="button"
                      data-reauth-provider={p}
                      aria-pressed={selected}
                      disabled={deleting}
                      onClick={() => { setChosenProvider(p); setDeleteError('') }}
                      className="flex items-center justify-center gap-2"
                      style={{
                        width: '100%', padding: 11, borderRadius: 11, fontSize: 13, fontWeight: 700,
                        cursor: deleting ? 'default' : 'pointer',
                        background: selected ? NAVY : '#fff',
                        color: selected ? '#fff' : NAVY,
                        border: `1.5px solid ${selected ? NAVY : LINE}`,
                      }}
                    >
                      {selected && <Check size={15} color="#fff" strokeWidth={2.6} />}
                      {p === 'apple'
                        ? (t('signIn.continueWithApple') || 'Continue with Apple')
                        : (t('signIn.continueWithGoogle') || 'Continue with Google')}
                    </button>
                  )
                })}
              </div>
            )}
            {deleteError && <p style={{ fontSize: 11, color: '#DC2626', margin: '8px 0 0' }}>{deleteError}</p>}
          </div>
        ) : (
        <div>
          <p style={{ fontSize: 11, fontWeight: 600, color: NAVY, margin: '0 0 8px' }}>
            {t('settingsMisc.enterPasswordToConfirm') || 'Enter your password to confirm'}
          </p>
          <div className="flex items-center gap-2" style={{ border: `1.5px solid ${LINE}`, borderRadius: 12, padding: '10px 14px' }}>
            <Lock size={14} color={SLATE4} className="flex-shrink-0" />
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => { setPassword(e.target.value); setDeleteError('') }}
              placeholder="••••••••"
              className="flex-1 outline-none text-xs bg-transparent"
              style={{ color: NAVY, border: 'none' }}
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="flex-shrink-0 p-0"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              {showPassword ? <EyeOff size={14} color={SLATE4} /> : <Eye size={14} color={SLATE4} />}
            </button>
          </div>
          {deleteError && <p style={{ fontSize: 11, color: '#DC2626', margin: '8px 0 0' }}>{deleteError}</p>}
        </div>
        )}

        {/* Reason (optional) */}
        <button
          onClick={reasonLocked ? undefined : () => setShowReasonModal(true)}
          style={{
            marginTop: 14, width: '100%', textAlign: 'left',
            border: reason ? `1.5px solid ${LBLUE}` : `1.5px dashed ${LINE}`,
            borderRadius: 12, padding: '11px 14px', background: reason ? ICE : 'none', cursor: 'pointer',
            fontSize: 12.5, color: reason ? BLUE : SLATE, fontWeight: reason ? 600 : 400,
          }}
        >
          {reason ? `${t('settingsMisc.reason') || 'Reason'}: ${reason}` : (t('settingsMisc.tellUsWhyOptional') || "Tell us why you're leaving (optional)")}
        </button>

        {/* Actions */}
        <div style={{ paddingTop: 20 }}>
          <button
            onClick={handleDelete}
            disabled={deleting || !providersKnown || (passwordRequired && !password) || choiceMissing}
            style={{
              width: '100%', padding: 13, borderRadius: 12, border: 'none',
              fontSize: 13, fontWeight: 700, marginBottom: 9,
              cursor: (deleting || !providersKnown || (passwordRequired && !password) || choiceMissing) ? 'default' : 'pointer',
              background: (deleting || !providersKnown || (passwordRequired && !password) || choiceMissing) ? '#FCA5A5' : '#DC2626',
              color: '#fff',
            }}
          >
            {t('settingsMisc.deleteMyAccount') || 'Delete my account'}
          </button>
          <button
            onClick={() => goBack()}
            disabled={deleting}
            style={{
              width: '100%', padding: 13, borderRadius: 12, fontSize: 13, fontWeight: 700,
              cursor: 'pointer', background: 'none', color: SLATE4, border: `1.5px solid ${LINE}`,
            }}
          >
            {t('common.cancel') || 'Cancel'}
          </button>
        </div>
      </div>

      {/* Reason modal */}
      {showReasonModal && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-50">
          <div className="bg-white w-full bottom-actions [--ba-min:0px]" style={{ maxWidth: 430, borderRadius: '22px 22px 0 0' }}>
            <div className="p-4 border-b border-gray-100">
              <h3 className="text-lg font-bold" style={{ color: NAVY }}>{t('settingsMisc.whyAreYouLeaving') || 'Why are you leaving?'}</h3>
              <p className="text-gray-500 text-sm">{t('settingsMisc.yourFeedbackHelpsUsImprove') || 'Your feedback helps us improve.'}</p>
            </div>
            <div className="p-2">
              {REASONS.map((r) => (
                <button key={r}
                  onClick={() => { setReason(r); setShowReasonModal(false) }}
                  className="w-full text-left px-4 py-3 rounded-lg"
                  style={{
                    background: reason === r ? ICE : 'transparent',
                    color: reason === r ? BLUE : '#475569',
                    fontWeight: reason === r ? 600 : 400, fontSize: 14, border: 'none', cursor: 'pointer',
                  }}>
                  {r}
                </button>
              ))}
            </div>
            <div className="p-4 border-t border-gray-100">
              <button onClick={() => setShowReasonModal(false)} className="w-full py-3 text-gray-500 font-medium"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                {t('common.skip') || 'Skip'}
              </button>
            </div>
          </div>
        </div>
        </OverlayPortal>
      )}

      {/* Confirm modal */}
      {showConfirmModal && (
        <OverlayPortal>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <div className="mx-auto mb-4 flex items-center justify-center"
              style={{ width: 64, height: 64, borderRadius: 999, background: '#FEF2F2' }}>
              <AlertTriangle size={32} color="#DC2626" />
            </div>
            <h3 className="text-xl font-bold text-center mb-2" style={{ color: NAVY }}>{t('settingsMisc.areYouSure') || 'Are you sure?'}</h3>
            <div style={{ marginBottom: 20 }}>
              <p className="text-center text-sm" style={{ color: SLATE, margin: '0 0 12px', lineHeight: 1.5 }}>
                {t('settingsMisc.deleteConfirmLead') || 'Before you delete, please read this carefully:'}
              </p>
              <ul style={{ margin: 0, paddingLeft: 18, textAlign: 'left' }}>
                <li style={{ fontSize: 12.5, color: NAVY, lineHeight: 1.55, marginBottom: 7, fontWeight: 700 }}>
                  {t('settingsMisc.deleteConfirmIrreversible') || "This is permanent and can't be undone."}
                </li>
                <li style={{ fontSize: 12.5, color: NAVY, lineHeight: 1.55, marginBottom: 7 }}>
                  {t('settingsMisc.deleteConfirmAppointments') || 'Any pending appointments will be cancelled.'}
                </li>
                <li style={{ fontSize: 12.5, color: NAVY, lineHeight: 1.55 }}>
                  {t('settingsMisc.deleteConfirmDownload') || "Download your records now if you want them — afterwards you'll need to contact admin to request a copy."}
                </li>
              </ul>
            </div>
            <div className="space-y-3">
              <button onClick={confirmDelete} disabled={deleting}
                className="w-full py-3 rounded-full font-semibold flex items-center justify-center gap-2"
                style={{ background: '#DC2626', color: 'white', border: 'none', cursor: deleting ? 'default' : 'pointer', opacity: deleting ? 0.8 : 1 }}>
                {deleting && <OrellaSpinner size={16} tone="inherit" />}
                {reauthing
                  ? (t('settingsMisc.confirmingItsYou') || `Signing in with ${providerLabel}…`)
                  : deleting ? (t('settingsMisc.deleting') || 'Deleting…')
                  : (t('settingsMisc.yesDeleteMyAccount') || 'Yes, delete my account')}
              </button>
              <button onClick={() => setShowConfirmModal(false)} disabled={deleting}
                className="w-full py-3 rounded-full font-semibold"
                style={{ border: `2px solid ${LINE}`, color: SLATE, background: 'none', cursor: 'pointer' }}>
                {t('settingsMisc.noKeepMyAccount') || 'No, keep my account'}
              </button>
            </div>
          </div>
        </div>
        </OverlayPortal>
      )}
    </div>
  )
}
